import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AgenciaDto } from '../models/agencia-dto';
import { BancoDto } from '../models/banco-dto';
import { BloqueioManualDto } from '../models/bloqueio-manual-dto';
import { BloqueioRestituicaoDto } from '../models/bloqueio-restituicao-dto';
import { CampoAlteravel } from '../models/campo-alteravel';
import { CondicaoDocumentoDto } from '../models/condicao-documento-dto';
import { DesmembramentoDto } from '../models/desmembramento-dto';
import { DocumentoArrecadacaoDto } from '../models/documento-arrecadacao-dto';
import { DocumentoArrecadacaoRetificacaoBase1TO } from '../models/documento-arrecadacao-retificacao-base1-to';
import { ModalidadeArrecadacaoDto } from '../models/modalidade-arrecadacao-dto';
import { NaturezaDto } from '../models/natureza-dto';
import { Pagamento } from '../models/pagamento';
import { PagamentoDto } from '../models/pagamento-dto';
import { ParametroCalculaReceitaValorTO } from '../models/parametro-calcula-receita-valor-to';
import { ParametrosPesquisaPagamento } from '../models/parametros-pesquisa';
import { ReceitaDto } from '../models/receita-dto';
import { ReceitaValorTO } from '../models/receita-valor-to';
import { RegiaoDto } from '../models/regiao-dto';
import { RetornoRetificacaoTO } from '../models/retorno-retificacao-to';
import { RetornoVerificaRetificacaoTO } from '../models/retorno-verifica-retificacao-to';
import { SistemaInteresseDto } from '../models/sistema-interesse-dto';
import { TipoDocumentoDto } from '../models/tipo-documento-dto';
import { TipoRetificacao } from '../models/tipo-retificacao';
import { UnidadeAdministrativaDto } from '../models/unidade-administrativa-dto';
import { ValoresReceita } from '../models/valoresReceita';

@Injectable()
export class PagamentoService {

  private url = environment.APIEndpoint;
  public atualizarListagemEmitter: EventEmitter<ParametrosPesquisaPagamento> = new EventEmitter();

  constructor(private httpClient: HttpClient) { }

  public atualizarListagemPagamento(parametros: ParametrosPesquisaPagamento): void {
    this.atualizarListagemEmitter.emit(parametros);
  }

  public obterTiposDocumentoArrecadacao(): Observable<TipoDocumentoDto[]> {
    return this.httpClient.get<TipoDocumentoDto[]>(`${this.url}/pagamentos/dominio/tipoDocumento/getAll`);
  }

  public obterCondicoesDocumentoArrecadacao(): Observable<CondicaoDocumentoDto[]> {
    return this.httpClient.get<CondicaoDocumentoDto[]>(`${this.url}/pagamentos/dominio/condicao/getAll`);
  }

  public obterReceitas(): Observable<ReceitaDto[]> {
    return this.httpClient.get<ReceitaDto[]>(`${this.url}/pagamentos/dominio/receita/getAll`);
  }

  public obterBancos(): Observable<BancoDto[]> {
    return this.httpClient.get<BancoDto[]>(`${this.url}/pagamentos/dominio/banco/getAll`);
  }

  public obterModalidadesArrecadacao(): Observable<ModalidadeArrecadacaoDto[]> {
    return this.httpClient.get<ModalidadeArrecadacaoDto[]>(`${this.url}/pagamentos/dominio/modalidade/getAll`);
  }

  public obterDelegacias(): Observable<UnidadeAdministrativaDto[]> {
    return this.httpClient.get<UnidadeAdministrativaDto[]>(`${this.url}/pagamentos/dominio/delegacia/getAll`);
  }

  public obterRegioes(): Observable<RegiaoDto[]> {
    return this.httpClient.get<RegiaoDto[]>(`${this.url}/pagamentos/dominio/regiao/getAll`);
  }

  public obterNaturezasDocumentoArrecadacao(): Observable<NaturezaDto[]> {
    return this.httpClient.get<NaturezaDto[]>(`${this.url}/pagamentos/dominio/natureza/getAll`);
  }

  public obterAgencias(banco: BancoDto): Observable<AgenciaDto[]> {
    return this.httpClient.post<AgenciaDto[]>(`${this.url}/pagamentos/dominio/agencia/getByBanco`, banco.codigo);
  }

  public consultarPagamentos(parametros: ParametrosPesquisaPagamento, firstIndex: number, maxResults: number): Observable<PagamentoDto[]> {
    return this.httpClient.post<PagamentoDto[]>(`${this.url}/pagamentos/search/${firstIndex}/${maxResults}`, parametros);
  }

  public obterDadosDeComposicao(pgtoNr: string): Observable<DesmembramentoDto[]> {
    return this.httpClient.get<DesmembramentoDto[]>(`${this.url}/pagamentos/desmembramento/searchByNumeroDocumento/${pgtoNr}`);
  }

  public obterSaldosDocumento(parametros: ParametrosPesquisaPagamento): Observable<number[]> {
    return this.httpClient.post<number[]>(`${this.url}/pagamentos/searchSaldosDocumento`, parametros);
  }

  public obterBloqueiosManuais(numeroDocumento: string): Observable<BloqueioManualDto[]> {
    return this.httpClient.get<BloqueioManualDto[]>(`${this.url}/pagamentos/bloqueiosManuais/${numeroDocumento}`);
  }

  public obterBloqueiosRestituicao(numeroDocumento: string): Observable<BloqueioRestituicaoDto[]> {
    return this.httpClient.get<BloqueioRestituicaoDto[]>(`${this.url}/pagamentos/bloqueiosRestituicao/${numeroDocumento}`);
  }

  public obterCamposAlteraveis(pagamento: Pagamento, tipoRetificacao: TipoRetificacao): Observable<CampoAlteravel[]> {
    let observable;

    if (tipoRetificacao == TipoRetificacao.COMPLETA) {
      observable = this.httpClient.post<CampoAlteravel[]>(`${this.url}/pagamentos/retificacao-completa/campos-alteraveis`, pagamento);
    } else {
      observable = this.httpClient.post<CampoAlteravel[]>(`${this.url}/pagamentos/retificacao-simplificada/campos-alteraveis`, pagamento);
    }

    return observable.pipe(
      map((cas: CampoAlteravel[]) => {
        const camposAlteraveis = {};

        cas.forEach(campo => {
          camposAlteraveis[campo.nomeCampo] = campo;
        });

        return camposAlteraveis;
      })
    );
  }

  public calcularValores(pagamentoAlterado: Pagamento): Observable<ValoresReceita[]> {
    return this.httpClient.post<ValoresReceita[]>(`${this.url}/pagamentos/valores-receitas`, pagamentoAlterado);
  }

  public realizarRetificacao(pagamentoAlterado: Pagamento, tipoRetificacao: TipoRetificacao): Observable<ValoresReceita[]> {
    if (tipoRetificacao == TipoRetificacao.COMPLETA) {
      return this.httpClient.put<ValoresReceita[]>(`${this.url}/pagamentos/retificacao-completa`, pagamentoAlterado);
    } else {
      return this.httpClient.put<ValoresReceita[]>(`${this.url}/pagamentos/retificacao-simplificada`, pagamentoAlterado);
    }
  }

  public verificarTrocaAssinalamento(documentoArrecadacao: DocumentoArrecadacaoDto): Observable<boolean> {
    const url = `${this.url}/pagamentos/trocaAssinalamento/verificarTrocaAssinalamento`;
    return this.httpClient.post<boolean>(url, documentoArrecadacao);
  }

  public obterRegistroTroca(numeroDocumento: string[]): Observable<DocumentoArrecadacaoDto> {
    const url = `${this.url}/pagamentos/trocaAssinalamento/consultaDocumentoListaNumeroRegistroTroca`;
    return this.httpClient.post<DocumentoArrecadacaoDto>(url, numeroDocumento[0]);
  }

  public obterListaSistemasInteresse(documentoArrecadacao: DocumentoArrecadacaoDto): Observable<SistemaInteresseDto[]> {
    const url = `${this.url}/pagamentos/trocaAssinalamento/sistemasInteresse/list`;
    return this.httpClient.post<SistemaInteresseDto[]>(url, documentoArrecadacao);
  }

  public obterSistemaInteresseIndicado(documentoArrecadacao: DocumentoArrecadacaoDto): Observable<SistemaInteresseDto> {
    const url = `${this.url}/pagamentos/trocaAssinalamento/sistemaInteresseIndicado`;
    return this.httpClient.post<SistemaInteresseDto>(url, documentoArrecadacao);
  }

  public trocarAssinalamento(documentoArrecadacao: DocumentoArrecadacaoDto): Observable<NonNullable<unknown>> {
    const url = `${this.url}/pagamentos/trocaAssinalamento/alterar`;
    return this.httpClient.post(url, documentoArrecadacao);
  }

  public verificaRetificacaoSimplificada(docArrecRetificaBase1: DocumentoArrecadacaoRetificacaoBase1TO): Observable<RetornoVerificaRetificacaoTO> {
    const url = `${this.url}/pagamentos/retificacao/verificaRetificacaoSimplificada`;
    return this.httpClient.post<RetornoVerificaRetificacaoTO>(url, docArrecRetificaBase1);
  }

  public retificaDocumentoSimplificada(docArrecRetificaBase1: DocumentoArrecadacaoRetificacaoBase1TO): Observable<RetornoRetificacaoTO> {
    const url = `${this.url}/pagamentos/retificacao/retificaDocumentoSimplificada`;
    return this.httpClient.post<RetornoRetificacaoTO>(url, docArrecRetificaBase1);
  }

  public isReceitaAgregadora(codigoReceita: number): Observable<boolean> {
    return this.httpClient.get<boolean>(`${this.url}/pagamentos/isReceitaAgregadora/${codigoReceita}`);
  }

  public verificaRetificacao(docArrecRetificaBase1: DocumentoArrecadacaoRetificacaoBase1TO): Observable<RetornoVerificaRetificacaoTO> {
    const url = `${this.url}/pagamentos/retificacao/verificaRetificacao`;
    return this.httpClient.post<RetornoVerificaRetificacaoTO>(url, docArrecRetificaBase1);
  }

  public retificaDocumento(docArrecRetificaBase1: DocumentoArrecadacaoRetificacaoBase1TO): Observable<RetornoRetificacaoTO> {
    const url = `${this.url}/pagamentos/retificacao/retificaDocumento`;
    return this.httpClient.post<RetornoRetificacaoTO>(url, docArrecRetificaBase1);
  }

  public calcularReceitas(parametroCalculaReceitaValorTO: ParametroCalculaReceitaValorTO): Observable<ReceitaValorTO[]> {
    const url = `${this.url}/pagamentos/retificacao/calculaListaReceitaValor`;
    return this.httpClient.post<ReceitaValorTO[]>(url, parametroCalculaReceitaValorTO);
  }
}
